import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './styles.module.scss';
import ReviewsHeader from './ReviewsHeader';
import PaginatedReviews from './PaginatedReviews';
import ReviewCardList from './ReviewCardList';
import { fetchProductReviews, updateAppliedReviewFiltersArray } from 'src/scripts/utils/reviews';
import { ProductReview, ProductReviewsResponse, AppliedReviewFilter, ReviewSort } from '../../types/reviews';
import ReviewsSearchAndFiltering from './ReviewSearchAndKeywords';
import { isEqual } from 'lodash';
import { useDebounce } from 'usehooks-ts';
import ReviewFilters from './ReviewFilters';
import AppliedProductReviewFilters from './AppliedProductReviewFilters';

const ProductReviews = () => {
  const [page, setPage] = useState<number>(0);
  const [sortValue, setSortValue] = useState<ReviewSort>(ReviewSort.RatingDesc);
  const productHandle = window?.server_pdp_data?.product?.handle || '';

  const [reviewData, setReviewData] = useState<ProductReviewsResponse | null>(null);
  const [reviews, setReviews] = useState<ProductReview[]>([]);
  const [selectedReviewFilters, setFilters] = useState<AppliedReviewFilter[]>([]);

  // Used to track initial page load
  const [initialLoading, setInitialLoading] = useState(true);
  // Loading state for each time reviews are fetched
  const [loading, setLoading] = useState<boolean>(false);
  // Used to determine if there are no reviews on the initial load, will not eval to true if filtered results are empty
  const [noReviewsOnInitialLoad, setNoReviewsOnInitialLoad] = useState(false);
  const hasCheckedInitialReviews = useRef(false);

  const [keywords, setKeywords] = useState<string[] | null>(null);
  const [activeKeyword, setActiveKeyword] = useState<string | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const debouncedSearchText = useDebounce<string>(searchTerm ?? '', 300);

  const loadReviews = useCallback(
    async (signal?: AbortSignal) => {
      if (initialLoading) setLoading(false);
      else setLoading(true);
      try {
        if (!productHandle) {
          throw new Error('Product handle is not available');
        }
        const response = await fetchProductReviews({
          productHandle,
          offset: page === 0 ? 0 : (page - 1) * 10 + 5, // Offset for pagination
          limit: page === 0 ? 5 : 10, // Load 5 reviews on first page, then 10 on subsequent pages
          sort: sortValue,
          feature: activeKeyword || undefined,
          q: debouncedSearchText || undefined,
          signal,
          filters: selectedReviewFilters,
        });

        // If there are no reviews on the initial load, set the flag
        if (page === 0 && !hasCheckedInitialReviews.current) {
          hasCheckedInitialReviews.current = true;
          if (response.reviews.length <= 0) {
            setNoReviewsOnInitialLoad(true);
          }
        }
        setReviewData(response);
        setKeywords(response.features || null);
        setReviews((prevReviews) => {
          // compare previous reviews with new ones to avoid unnecessary state changes
          const nextReviews = page === 0 ? response.reviews : [...prevReviews, ...response.reviews];
          return isEqual(prevReviews, nextReviews) ? prevReviews : nextReviews;
        });
      } catch (err) {
        console.error(err);
        if (!(err instanceof DOMException && err.name === 'AbortError')) {
          setReviewData(null);
          setReviews([]);
        }
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    },
    [page, productHandle, sortValue, activeKeyword, debouncedSearchText, selectedReviewFilters, initialLoading]
  );

  useEffect(() => {
    const controller = new AbortController();
    loadReviews(controller.signal);
    return () => controller.abort();
  }, [loadReviews]);

  // Reset page and reviews when keyword, search, or sort method changes
  useEffect(() => {
    setPage((prev) => (prev !== 0 ? 0 : prev));
  }, [activeKeyword, debouncedSearchText, sortValue, selectedReviewFilters]);

  const handleLoadMore = () => {
    setPage((page) => page + 1);
  };

  const toggleAppliedProductReviewFilter = (
    appliedFilters: AppliedReviewFilter[],
    key: string,
    value: string,
    checked: boolean
  ) => {
    setFilters(updateAppliedReviewFiltersArray(appliedFilters, key, value, checked));
  };

  const onClearAllProductReviewFilters = () => {
    setActiveKeyword(undefined);
    setFilters([]);
    setSearchTerm(undefined);
  };

  const productReviewFilters = reviewData?.filters || [];

  if (initialLoading) {
    return null;
  }

  if (noReviewsOnInitialLoad || !reviewData) {
    return (
      <p className="body my-xl px-md">
        {noReviewsOnInitialLoad ? 'This product has not been reviewed yet.' : 'Unable to load reviews at this time.'}
      </p>
    );
  }

  return (
    <div id="ProductReviews" className={styles.productReviewsContainer}>
      <ReviewsHeader productHandle={productHandle} />
      <ReviewsSearchAndFiltering
        keywords={keywords}
        activeKeyword={activeKeyword}
        setActiveKeyword={setActiveKeyword}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
      />
      <ReviewFilters
        filters={productReviewFilters}
        toggleAppliedProductReviewFilter={toggleAppliedProductReviewFilter}
        onClearAllProductReviewFilters={onClearAllProductReviewFilters}
        selectedFilters={selectedReviewFilters}
        sortValue={sortValue}
        setSortValue={setSortValue}
        keywords={keywords}
        activeKeyword={activeKeyword}
        setActiveKeyword={setActiveKeyword}
        loading={loading}
        productReviewsTotalCount={reviewData?.total_filtered_results || 0}
      />
      <AppliedProductReviewFilters
        filters={productReviewFilters}
        appliedFilters={selectedReviewFilters}
        toggleAppliedProductReviewFilter={toggleAppliedProductReviewFilter}
        onClearAllProductReviewFilters={onClearAllProductReviewFilters}
        activeKeyword={activeKeyword}
        setActiveKeyword={setActiveKeyword}
      />
      <PaginatedReviews
        productReviewsCount={reviews.length || 0}
        productReviewsTotalCount={reviewData?.total_filtered_results || 0}
        loading={loading}
        handleLoadMore={handleLoadMore}
        sortValue={sortValue}
        setSortValue={setSortValue}
      >
        <ReviewCardList
          productReviews={reviews || []}
          loading={loading}
          onClearAllProductReviewFilters={onClearAllProductReviewFilters}
        />
      </PaginatedReviews>
    </div>
  );
};

export default ProductReviews;
